import { useState, useEffect, useCallback, useRef } from 'react';
import { useLiveRefresh } from '@/contexts/WebSocketContext';
import { isAdmin } from '@/utils/roles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LogOut,
  UserCog,
  Bell,
  Clock,
  CheckCircle2,
  ChefHat,
  Flame,
  RefreshCw,
  Timer,
  AlertCircle,
  Utensils,
  Play,
  Volume2,
  VolumeX,
  ArrowLeft,
  Shield,
  Printer,
  Columns,
  LayoutGrid,
  Zap,
  AlertTriangle,
  Coffee
} from 'lucide-react';
import axios from 'axios';
import ModernKitchen from '@/modern/staff/ModernKitchen';
import { useIsModern } from '@/modern/shared';
import { toast } from 'sonner';
import { KITCHEN_URGENCY, kitchenLevel } from '@/theme/statusColors';
import { useKitchenThresholds } from '@/components/kitchen/useKitchenThresholds';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

function KitchenView() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { playSound, toggleSounds } = useNotificationSound();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('soundsEnabled') !== 'false';
  });
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const previousOrdersRef = useRef([]);
  const [printingOrder, setPrintingOrder] = useState(null);
  const [showComandaPreview, setShowComandaPreview] = useState(false);
  const [comandaHtml, setComandaHtml] = useState('');
  
  // New UX state
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('kitchenViewMode') || 'kanban';
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    if (user && !['kitchen', 'super_admin', 'business_admin'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);
  useLiveRefresh(() => fetchData(), 10000);

  // Real-time timer update every second
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timerInterval);
  }, []);

  // Persist view mode
  useEffect(() => {
    localStorage.setItem('kitchenViewMode', viewMode);
  }, [viewMode]);

  const fetchData = async () => {
    try {
      const [ordersRes, businessRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/business`)
      ]);
      
      const kitchenOrders = ordersRes.data.filter(
        order => order.status === 'Confirmado' || order.status === 'En preparación'
      );
      
      // Check for new orders
      const previousIds = new Set(previousOrdersRef.current.map(o => o.order_id));
      const newOrders = kitchenOrders.filter(o => !previousIds.has(o.order_id) && o.status === 'Confirmado');
      
      if (newOrders.length > 0 && previousOrdersRef.current.length > 0) {
        // Play sound for new orders
        if (soundEnabled) {
          playSound('newOrder');
        }
        // Mark as new for animation
        setNewOrderIds(new Set(newOrders.map(o => o.order_id)));
        // Clear animation after 3 seconds
        setTimeout(() => setNewOrderIds(new Set()), 3000);
      }
      
      previousOrdersRef.current = kitchenOrders;
      setOrders(kitchenOrders);
      setBusiness(businessRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSound = (enabled) => {
    setSoundEnabled(enabled);
    toggleSounds(enabled);
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus });
      toast.success(newStatus === 'En preparación' ? '¡Preparando!' : '¡Listo para servir!');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Print comanda function - shows preview first based on settings
  const handlePrintComanda = async (orderId) => {
    setPrintingOrder(orderId);
    try {
      // Get settings to check if preview is enabled
      let showPreview = true;
      try {
        const settingsRes = await axios.get(`${API}/business/settings`);
        showPreview = settingsRes.data?.show_print_preview !== false;
      } catch (e) {}
      
      const response = await axios.post(`${API}/print/order/${orderId}`, {
        area: 'kitchen'
      });
      
      const { html } = response.data;
      
      if (html) {
        if (showPreview) {
          setComandaHtml(html);
          setShowComandaPreview(true);
        } else {
          const printWindow = window.open('', '_blank', 'width=400,height=600');
          printWindow.document.write(html);
          printWindow.document.close();
        }
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Error al imprimir comanda');
    } finally {
      setPrintingOrder(null);
    }
  };

  // Actually print after preview
  const handleConfirmPrintComanda = () => {
    if (!comandaHtml) return;
    
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(comandaHtml);
    printWindow.document.close();
    setShowComandaPreview(false);
    setComandaHtml('');
  };

  const getTimeSince = (dateString) => {
    const created = new Date(dateString);
    const diffMs = currentTime - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    
    // For very old orders (> 24 hours), show a simpler format
    if (diffMins >= 1440) { // > 24 hours
      const days = Math.floor(diffMins / 1440);
      return { text: `${days}d+`, mins: diffMins };
    }
    if (diffMins >= 60) {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return { text: `${hours}h ${mins}m`, mins: diffMins };
    }
    return { text: `${diffMins}:${diffSecs.toString().padStart(2, '0')}`, mins: diffMins };
  };
  const kitchenT = useKitchenThresholds();

  const getUrgencyLevel = (dateString) => kitchenLevel(dateString, currentTime, kitchenT);

  // Separate orders by status
  const pendingOrders = orders.filter(o => o.status === 'Confirmado');
  const preparingOrders = orders.filter(o => o.status === 'En preparación');
  const pendingCount = pendingOrders.length;
  const preparingCount = preparingOrders.length;
  
  // Calculate total workload
  const totalItems = orders.reduce((sum, o) => sum + (o.items?.reduce((s, i) => s + i.quantity, 0) || 0), 0);
  const criticalCount = orders.filter(o => getUrgencyLevel(o.created_at) === 'critical').length;
  const warningCount = orders.filter(o => getUrgencyLevel(o.created_at) === 'warning').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-orange-600 dark:text-orange-300">Cargando cocina...</p>
        </div>
      </div>
    );
  }

  // Reusable Order Card Component
  const OrderCard = ({ order, compact = false }) => {
    const urgency = getUrgencyLevel(order.created_at);
    const isPreparing = order.status === 'En preparación';
    const isNew = newOrderIds.has(order.order_id);
    const timeInfo = getTimeSince(order.created_at);
    const isExpanded = expandedOrder === order.order_id;

    return (
      <motion.div
        key={order.order_id}
        initial={isNew ? { scale: 0.8, opacity: 0 } : false}
        animate={{ 
          scale: isNew ? [1, 1.05, 1] : 1, 
          opacity: 1,
        }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.3 }}
        layout
      >
        <Card 
          data-testid={`order-card-${order.order_id}`}
          data-urgency={urgency}
          className={`relative overflow-hidden transition-all duration-300 cursor-pointer ${
            urgency === 'normal' && isPreparing ? KITCHEN_URGENCY.preparing.card : KITCHEN_URGENCY[urgency].card
          } ${isNew ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-background animate-pulse' : ''}`}
          onClick={() => setExpandedOrder(isExpanded ? null : order.order_id)}
        >
          {/* Urgency indicator bar */}
          <div className={`absolute top-0 left-0 right-0 h-1.5 ${
            urgency === 'normal' && isPreparing ? KITCHEN_URGENCY.preparing.bar : KITCHEN_URGENCY[urgency].bar
          }`} />

          <CardHeader className={`${compact ? 'p-3 pb-2' : 'p-4 pb-2'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {urgency === 'critical' && (
                    <AlertTriangle className="w-5 h-5 text-red-400 animate-bounce" />
                  )}
                  <CardTitle className={`font-black ${compact ? 'text-xl' : 'text-2xl'} truncate ${
                    urgency === 'critical' ? 'text-white' :
                    urgency === 'warning' ? 'text-white' :
                    'text-white'
                  }`}>
                    {order.unit_name || 'Mesa'}
                  </CardTitle>
                </div>
                <p className={`text-xs font-mono ${KITCHEN_URGENCY[urgency].code}`}>
                  #{order.order_id?.slice(-6)}
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className={`flex items-center gap-1 px-2 py-1 rounded-lg mb-1 ${KITCHEN_URGENCY[urgency].timer}`}>
                  <Timer className={`w-4 h-4 ${urgency === 'critical' ? 'animate-pulse' : ''}`} />
                  <span className={`font-mono font-bold ${compact ? 'text-sm' : 'text-lg'}`}>
                    {timeInfo.text}
                  </span>
                </div>
                <Badge className={`text-xs ${
                  isPreparing 
                    ? 'bg-orange-500/30 text-orange-200 border border-orange-500/50' 
                    : 'bg-blue-500/30 text-blue-200 border border-blue-500/50'
                }`}>
                  {isPreparing ? '🔥 Preparando' : '⏳ Pendiente'}
                </Badge>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className={`${compact ? 'p-3 pt-0' : 'p-4 pt-2'}`}>
            {/* Items - Scrollable */}
            <div className={`${isExpanded || !compact ? 'max-h-[200px]' : 'max-h-[100px]'} overflow-y-auto pr-1 space-y-2 transition-all duration-300`}>
              {order.items?.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-start gap-2 p-2 rounded-lg ${
                    urgency === 'critical' ? 'bg-black/40 border border-red-500/30' :
                    urgency === 'warning' ? 'bg-black/30 border border-amber-500/20' :
                    'bg-black/20 border border-white/5'
                  } ${item.notes ? 'ring-1 ring-yellow-500/50' : ''}`}
                >
                  <div className={`${compact ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-lg'} rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-bold shrink-0`}>
                    {item.quantity}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-white ${compact ? 'text-sm' : ''} truncate`}>
                      {item.product_name}
                    </p>
                    {item.variant && (
                      <p className="text-xs text-slate-300 truncate">{item.variant}</p>
                    )}
                    {item.notes && (
                      <p className="text-xs text-yellow-300 mt-0.5 flex items-start gap-1">
                        <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{item.notes}</span>
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Order notes */}
            {order.notes && (
              <div className="mt-2 p-2 rounded-lg bg-yellow-500/20 border border-yellow-500/40">
                <p className="text-sm text-yellow-300 flex items-start gap-2">
                  <span>📝</span>
                  <span>{order.notes}</span>
                </p>
              </div>
            )}

            {/* Actions - Always visible, larger touch targets */}
            <div className={`mt-3 ${compact ? 'space-y-1.5' : 'space-y-2'}`}>
              {isPreparing ? (
                <Button 
                  data-testid={`order-ready-btn-${order.order_id}`}
                  className={`w-full ${compact ? 'h-12 text-base' : 'h-14 text-lg'} font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 shadow-lg shadow-green-500/30 active:scale-95 transition-transform`}
                  onClick={(e) => { e.stopPropagation(); updateOrderStatus(order.order_id, 'Listo'); }}
                >
                  <CheckCircle2 className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} mr-2`} />
                  ¡LISTO PARA SERVIR!
                </Button>
              ) : (
                <Button 
                  data-testid={`order-prepare-btn-${order.order_id}`}
                  className={`w-full ${compact ? 'h-12 text-base' : 'h-14 text-lg'} font-bold bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 shadow-lg shadow-orange-500/30 active:scale-95 transition-transform`}
                  onClick={(e) => { e.stopPropagation(); updateOrderStatus(order.order_id, 'En preparación'); }}
                >
                  <Flame className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} mr-2`} />
                  PREPARAR
                </Button>
              )}
              <Button 
                variant="outline"
                className={`w-full ${compact ? 'h-9' : 'h-10'} bg-black/20 hover:bg-black/40 border-white/10`}
                onClick={(e) => { e.stopPropagation(); handlePrintComanda(order.order_id); }}
                disabled={printingOrder === order.order_id}
              >
                <Printer className="w-4 h-4 mr-2" />
                {printingOrder === order.order_id ? 'Imprimiendo...' : 'Comanda'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <div data-surface="dark" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Enhanced Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-xl border-b border-orange-500/30">
        <div className="px-3 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Back to Dashboard */}
            {isAdmin(user) && (
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white hover:bg-white/10"
              title="Volver al Panel"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            )}
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/40">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-black text-xl text-white tracking-tight">COCINA</h1>
              <p className="text-xs text-orange-400/80 truncate max-w-[150px]">{business?.name}</p>
            </div>
          </div>

          {/* Center Stats - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            {criticalCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/30 border border-red-500/50 animate-pulse">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="font-bold text-red-300">{criticalCount}</span>
                <span className="text-red-400 text-sm">Urgente</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 border border-blue-500/40">
              <Clock className="w-4 h-4 text-blue-400" />
              <span className="font-bold text-blue-300">{pendingCount}</span>
              <span className="text-blue-400 text-sm">Pendientes</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/20 border border-orange-500/40">
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="font-bold text-orange-300">{preparingCount}</span>
              <span className="text-orange-400 text-sm">Preparando</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-700/50 border border-slate-600">
              <Utensils className="w-4 h-4 text-slate-400" />
              <span className="font-bold text-slate-300">{totalItems}</span>
              <span className="text-slate-400 text-sm">Items</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="hidden md:flex items-center bg-slate-800 rounded-lg p-1 border border-slate-700">
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 ${viewMode === 'kanban' ? 'bg-orange-500/30 text-orange-400' : 'text-slate-400 hover:text-white'}`}
                onClick={() => setViewMode('kanban')}
                title="Vista Kanban"
                data-testid="view-kanban-btn"
              >
                <Columns className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 ${viewMode === 'grid' ? 'bg-orange-500/30 text-orange-400' : 'text-slate-400 hover:text-white'}`}
                onClick={() => setViewMode('grid')}
                title="Vista Grilla"
                data-testid="view-grid-btn"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>

            {/* Sound Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className={`h-9 w-9 ${soundEnabled ? 'text-green-400 bg-green-500/20' : 'text-slate-500 bg-slate-800'}`}
              onClick={() => handleToggleSound(!soundEnabled)}
              title={soundEnabled ? 'Sonido activado' : 'Sonido desactivado'}
              data-testid="sound-toggle-btn"
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>

            <Button 
              variant="ghost" 
              size="icon" 
              onClick={fetchData}
              className="h-9 w-9 text-slate-400 hover:text-white hover:bg-white/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/perfil')}
              className="h-9 w-9 text-slate-400 hover:text-white hover:bg-white/10"
              data-testid="profile-link"
              title="Mi perfil y apariencia"
            >
              <UserCog className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              className="h-9 w-9 text-slate-400 hover:text-red-400 hover:bg-red-500/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Mobile Stats Bar */}
        <div className="lg:hidden px-3 pb-3 flex gap-2 overflow-x-auto">
          {criticalCount > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/30 border border-red-500/50 animate-pulse shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="font-bold text-red-300">{criticalCount}</span>
              <span className="text-red-400 text-xs">Urgente</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/20 border border-blue-500/40 shrink-0">
            <Clock className="w-4 h-4 text-blue-400" />
            <span className="font-bold text-blue-300">{pendingCount}</span>
            <span className="text-blue-400 text-xs">Pend.</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/20 border border-orange-500/40 shrink-0">
            <Flame className="w-4 h-4 text-orange-400" />
            <span className="font-bold text-orange-300">{preparingCount}</span>
            <span className="text-orange-400 text-xs">Prep.</span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-700/50 border border-slate-600 shrink-0">
            <Utensils className="w-4 h-4 text-slate-400" />
            <span className="font-bold text-slate-300">{totalItems}</span>
            <span className="text-slate-400 text-xs">Items</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-3 md:p-4 lg:p-6">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[60vh]">
            <div className="w-32 h-32 rounded-full bg-slate-800/50 flex items-center justify-center mb-6 border border-slate-700">
              <Coffee className="w-16 h-16 text-slate-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-400 mb-2">Sin pedidos pendientes</h2>
            <p className="text-slate-500 text-center max-w-md">
              Los nuevos pedidos aparecerán aquí automáticamente con una notificación sonora
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              Escuchando nuevos pedidos...
            </div>
          </div>
        ) : viewMode === 'kanban' ? (
          /* KANBAN VIEW - Two Columns */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {/* Pending Column */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4 px-2">
                <div className="w-10 h-10 rounded-xl bg-blue-500/30 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h2 className="font-bold text-lg text-white">Pendientes</h2>
                  <p className="text-sm text-slate-400">{pendingCount} pedidos esperando</p>
                </div>
              </div>
              <div className="space-y-3" data-testid="pending-orders-column">
                <AnimatePresence mode="popLayout">
                  {pendingOrders
                    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                    .map((order) => (
                      <OrderCard key={order.order_id} order={order} compact={pendingOrders.length > 3} />
                    ))}
                </AnimatePresence>
                {pendingOrders.length === 0 && (
                  <div className="text-center py-12 text-slate-500 border border-dashed border-slate-700 rounded-xl">
                    <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No hay pedidos pendientes</p>
                  </div>
                )}
              </div>
            </div>

            {/* Preparing Column */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-4 px-2">
                <div className="w-10 h-10 rounded-xl bg-orange-500/30 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h2 className="font-bold text-lg text-white">En Preparación</h2>
                  <p className="text-sm text-slate-400">{preparingCount} en cocina</p>
                </div>
              </div>
              <div className="space-y-3" data-testid="preparing-orders-column">
                <AnimatePresence mode="popLayout">
                  {preparingOrders
                    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                    .map((order) => (
                      <OrderCard key={order.order_id} order={order} compact={preparingOrders.length > 3} />
                    ))}
                </AnimatePresence>
                {preparingOrders.length === 0 && (
                  <div className="text-center py-12 text-slate-500 border border-dashed border-slate-700 rounded-xl">
                    <Flame className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No hay pedidos en preparación</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* GRID VIEW - Original style but improved */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="orders-grid">
            <AnimatePresence mode="popLayout">
              {orders
                .sort((a, b) => {
                  // Sort: critical first, then by time
                  const urgencyA = getUrgencyLevel(a.created_at);
                  const urgencyB = getUrgencyLevel(b.created_at);
                  const urgencyOrder = { critical: 0, warning: 1, normal: 2 };
                  if (urgencyOrder[urgencyA] !== urgencyOrder[urgencyB]) {
                    return urgencyOrder[urgencyA] - urgencyOrder[urgencyB];
                  }
                  return new Date(a.created_at) - new Date(b.created_at);
                })
                .map((order) => (
                  <OrderCard key={order.order_id} order={order} />
                ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Comanda Preview Modal */}
      <Dialog open={showComandaPreview} onOpenChange={setShowComandaPreview}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-orange-500" />
              Vista Previa de Comanda
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto bg-white rounded-lg border shadow-inner my-4" style={{ maxHeight: '50vh' }}>
            <iframe
              srcDoc={comandaHtml}
              title="Comanda Preview"
              className="w-full h-full min-h-[300px]"
              style={{ border: 'none' }}
            />
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowComandaPreview(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmPrintComanda}
              className="bg-gradient-to-r from-orange-500 to-red-600"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function KitchenViewPage() {
  return useIsModern() ? <ModernKitchen /> : <KitchenView />;
}
